import { ExternalLink } from "lucide-react";

import { KpiTile } from "@/components/cinch/KpiTile";

import type { PipelineBannerData } from "../api";
import { fmtCents, fmtCount } from "../format";
import { WowChip } from "./WowChip";

const HUBSPOT_DEALS_URL = "https://app.hubspot.com/contacts/21771408/deals";

/** Lookup into the BE's flattened week-over-week map. */
export type WowLookup = (key: string) => number | null;

/**
 * Opens a tile's drill-down. `which` names the pipeline row; `stage` is set only
 * when a stage tile was clicked. All three tiles in a row pass the same `which`
 * with no stage — they describe one deal set.
 */
export type OpenTile = (
  which: "sales" | "partner" | "cs",
  stage?: string,
) => void;

/**
 * Top-of-page banner: the Sales Pipeline trio + per-stage breakdown, then one
 * trio per secondary pipeline (Partner = "Reseller Deals", CS = "Customer
 * Success"). Those two get their own rows because their deals never appear in
 * the Sales Pipeline banner — without them a partner or CS rep's pipeline reads
 * as empty.
 *
 * Every card carries a week-over-week chip; `wow` returns null for any card the
 * BE judged incomparable (no prior snapshot, or a card that shipped this week),
 * and WowChip renders nothing in that case.
 */
export function PipelineBanner({
  data,
  partner,
  cs,
  wow,
  onOpenTile,
}: {
  data: PipelineBannerData;
  partner?: PipelineBannerData;
  cs?: PipelineBannerData;
  wow?: WowLookup;
  onOpenTile?: OpenTile;
}) {
  const w: WowLookup = wow ?? (() => null);
  const open = (which: "sales" | "partner" | "cs", stage?: string) =>
    onOpenTile ? () => onOpenTile(which, stage) : undefined;
  return (
    <section aria-label="Open pipeline banner" className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          label="Total open pipeline"
          value={<span className="text-power">{fmtCents(data.total_amount_cents)}</span>}
          sub={
            <SubWithChip
              // "Sales Qualified onward", not the three tiled stages: the total
              // is SQO-on, which also picks up Verbal Commit (no tile of its
              // own). Saying "SQ to Contract Neg." here understated what the
              // number actually contains.
              text={`${data.pipeline_name} → Sales Qualified onward`}
              delta={w("banner.total")}
            />
          }
          onClick={open("sales")}
        />
        <KpiTile
          label="Total open deals"
          value={fmtCount(data.total_deal_count)}
          sub={
            <SubWithChip
              text={<HubspotLink />}
              delta={w("banner.deals")}
              kind="count"
            />
          }
          onClick={open("sales")}
        />
        <KpiTile
          label="Avg deal size"
          value={fmtCents(data.avg_deal_cents)}
          // Average over the same deals as the total beside it, so it can't be
          // "across all stages" — the pre-SQO deals are out of both.
          sub={
            <SubWithChip text="Sales Qualified onward" delta={w("banner.avg")} />
          }
          onClick={open("sales")}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {data.stages.map((s) => (
          <KpiTile
            key={s.stage_name}
            label={s.stage_name}
            value={fmtCents(s.amount_cents)}
            sub={
              <SubWithChip
                text={`${fmtCount(s.deal_count)} deals`}
                delta={w(`stage.${s.stage_name}`)}
              />
            }
            onClick={open("sales", s.stage_name)}
          />
        ))}
      </div>
      {partner && (
        <SecondaryPipeline
          heading="Partner pipeline"
          sub="Reseller deals"
          data={partner}
          wow={w}
          prefix="partner"
          onOpen={open("partner")}
        />
      )}
      {cs && (
        <SecondaryPipeline
          heading="Customer Success pipeline"
          sub="Upsells & replacements"
          data={cs}
          wow={w}
          prefix="cs"
          onOpen={open("cs")}
        />
      )}
    </section>
  );
}

function SecondaryPipeline({
  heading,
  sub,
  data,
  wow,
  prefix,
  onOpen,
}: {
  heading: string;
  sub: string;
  data: PipelineBannerData;
  wow: WowLookup;
  prefix: string;
  onOpen?: () => void;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {heading}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <KpiTile
          label="Total open pipeline"
          value={<span className="text-power">{fmtCents(data.total_amount_cents)}</span>}
          sub={
            <SubWithChip
              text={`${sub} · all stages`}
              delta={wow(`${prefix}.total`)}
            />
          }
          onClick={onOpen}
        />
        <KpiTile
          label="Total open deals"
          value={fmtCount(data.total_deal_count)}
          sub={
            <SubWithChip
              text={<HubspotLink />}
              delta={wow(`${prefix}.deals`)}
              kind="count"
            />
          }
          onClick={onOpen}
        />
        <KpiTile
          label="Avg deal size"
          value={fmtCents(data.avg_deal_cents)}
          sub={
            <SubWithChip text="Across all stages" delta={wow(`${prefix}.avg`)} />
          }
          onClick={onOpen}
        />
      </div>
    </div>
  );
}

function SubWithChip({
  text,
  delta,
  kind = "money",
}: {
  text: React.ReactNode;
  delta: number | null;
  kind?: "money" | "count";
}) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <span>{text}</span>
      <WowChip delta={delta} kind={kind} />
    </span>
  );
}

function HubspotLink() {
  return (
    <a
      className="inline-flex items-center gap-1 hover:text-power"
      href={HUBSPOT_DEALS_URL}
      target="_blank"
      rel="noreferrer"
    >
      Open in HubSpot <ExternalLink className="h-3 w-3" />
    </a>
  );
}
